import { useEffect, useRef } from "react";
import { App as AppInfo } from "@capacitor/app";
import { Capacitor } from "@capacitor/core";
import { Device } from "@capacitor/device";
import { LocalNotifications } from "@capacitor/local-notifications";
import {
  ActionPerformed,
  PushNotifications,
  RegistrationError,
  Token,
} from "@capacitor/push-notifications";
import { FullStory, isInitialized as isFullstoryInitialized } from "@fullstory/browser";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { AxiosError } from "axios";
import { useHistory } from "react-router";

import { useAuth } from "~components";
import { createRequestsUrl, createRequestUrl } from "~pages";
import { DeviceInfoPayload, NotificationsPayload, PUT, trackPush } from "~utils";

type SwingNotificationData = {
  appVersion: string;
  deviceToken: string;
  deviceType: string;
  deviceId: string;
};

export function useRegisterPushNotifications() {
  const {
    userInfo: { isAlias },
    refreshUser,
  } = useAuth();
  const queryClient = useQueryClient();
  const history = useHistory();

  const swingPushNotificationData = useRef<SwingNotificationData>({
    appVersion: "",
    deviceId: "",
    deviceToken: "",
    deviceType: "",
  });

  // Track whether we've successfully registered with APNS/FCM this app run.
  // This prevents calling APIs that rely on registration before it happens.
  const hasRegisteredForRemoteNotifications = useRef(false);

  const { mutate: updateNotificationsSettings } = useMutation<
    { data: NotificationsPayload },
    AxiosError,
    NotificationsPayload
  >({
    mutationFn: (payload: NotificationsPayload) => PUT("/api/sub/notifications", payload),
    onSuccess: (updatedSettingsData) => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      queryClient.setQueryData(["userData"], (oldUserData: any) => ({
        ...oldUserData,
        notificationsSettings: updatedSettingsData.data.notificationsSettings,
      }));
      refreshUser();
    },
  });

  const { mutate: updateDeviceInfo } = useMutation<
    { data: DeviceInfoPayload },
    AxiosError,
    DeviceInfoPayload
  >({
    mutationFn: (payload: DeviceInfoPayload) => PUT("/api/sub/device-info", payload),
  });

  // Log device information
  useEffect(() => {
    if (isAlias || Capacitor.getPlatform() === "web") return;

    const logDeviceInfo = async () => {
      const deviceInfo = await Device.getInfo();
      const deviceId = await Device.getId();

      swingPushNotificationData.current.deviceId = deviceId.identifier;
      swingPushNotificationData.current.deviceType = `${deviceInfo.platform}: ${deviceInfo.manufacturer} ${deviceInfo.name} ${deviceInfo.model}`;
    };

    void logDeviceInfo();
  }, [isAlias]);

  // Log app information
  useEffect(() => {
    if (isAlias || Capacitor.getPlatform() === "web") return;

    const logAppInfo = async () => {
      const appInfo = await AppInfo.getInfo();
      swingPushNotificationData.current.appVersion = appInfo.version;
    };

    void logAppInfo();
  }, [isAlias]);

  // Push Notification EventListeners
  useEffect(() => {
    if (isAlias || Capacitor.getPlatform() === "web") return;

    const addPushNotificationListeners = async () => {
      await PushNotifications.addListener("registration", (token: Token) => {
        hasRegisteredForRemoteNotifications.current = true;

        swingPushNotificationData.current.deviceToken = token.value;

        updateDeviceInfo({
          model: swingPushNotificationData.current.deviceType,
          appVersion: swingPushNotificationData.current.appVersion,
          idf: swingPushNotificationData.current.deviceId,
          token: swingPushNotificationData.current.deviceToken,
        });
      });

      await PushNotifications.addListener("registrationError", (error: RegistrationError) => {
        // eslint-disable-next-line no-console
        console.error(`Error on notification registration: ${error.error}`);
      });

      await PushNotifications.addListener("pushNotificationReceived", (nt) => {
        trackPush("delivered", nt);
      });

      // Handle action when a user taps on a notification (background -> open)
      await PushNotifications.addListener(
        "pushNotificationActionPerformed",
        (notification: ActionPerformed) => {
          trackPush("opened", notification.notification);
          if (notification.notification.data["sub-req"]) {
            const requestUrl = createRequestUrl(notification.notification.data["sub-req"]);
            history.push(requestUrl);

            if (isFullstoryInitialized()) {
              FullStory("trackEvent", {
                name: "Swing Subs Mobile Notification Opened",
                properties: {
                  subReqID: notification.notification.data["sub-req"],
                },
              });
            }
          } else {
            history.push(createRequestsUrl());
          }
        },
      );
    };

    void addPushNotificationListeners();

    return () => {
      void PushNotifications.removeAllListeners();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAlias]);

  // Clear delivered notifications when returning to foreground (but only after remote registration)
  useEffect(() => {
    if (isAlias || Capacitor.getPlatform() === "web") return;

    let handle: Awaited<ReturnType<typeof AppInfo.addListener>> | null = null;

    (async () => {
      handle = await AppInfo.addListener("appStateChange", ({ isActive }) => {
        if (!isActive) return;
        if (!hasRegisteredForRemoteNotifications.current) return;

        void PushNotifications.removeAllDeliveredNotifications().catch((e) => {
          // eslint-disable-next-line no-console
          console.error("removeAllDeliveredNotifications failed:", e);
        });
      });
    })();

    return () => {
      void (async () => {
        if (handle) await handle.remove();
      })();
    };
  }, [isAlias]);

  // Register for push notifications (safe on repeat; only prompts when needed)
  useEffect(() => {
    if (isAlias || Capacitor.getPlatform() === "web") return;

    const registerForPush = async () => {
      try {
        const platform = Capacitor.getPlatform();

        if (platform === "ios") {
          // iOS: PushNotifications permissions reflect actual APNS permission
          const perm = await PushNotifications.checkPermissions();

          if (perm.receive === "prompt") {
            const req = await PushNotifications.requestPermissions();
            if (req.receive !== "granted") return;
            updateNotificationsSettings({ notificationsSettings: { push: true } });
          } else if (perm.receive === "denied") {
            return;
          }

          // Always register when granted (including "already granted" on subsequent launches)
          await PushNotifications.register();
          return;
        }

        // Android:
        // - On Android 12 and below, push permission is effectively always "granted" for FCM
        // - LocalNotifications can reflect user-level notification settings more accurately
        const localPerm = await LocalNotifications.checkPermissions();

        if (localPerm.display === "prompt" || localPerm.display === "prompt-with-rationale") {
          const req = await PushNotifications.requestPermissions();
          if (req.receive === "denied") return;

          await PushNotifications.register();
          updateNotificationsSettings({ notificationsSettings: { push: true } });
          return;
        }

        // If already granted / allowed, still register to ensure we get a token in this app run.
        // This is safe and prevents "event ... not called" issues in flows that assume registration.
        await PushNotifications.register();
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error("Error registering for push notifications:", error);
      }
    };

    void registerForPush();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAlias]);
}
